import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { get, head, isEmpty } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import IconSpinner from "components/IconSpinner";
import {
  pendingEmployerRequiredUpdates,
  requiresAdministrativeUpdates,
} from "store/selectors/employer";
import { employerConstants } from "actions/types";
import OrderFormAgreements from "pages/dashboards/employerDashboard/requiredUpdates/OrderFormAgreements";
import { getEmployerRequiredUpdates } from "actions/employerActions";
import type { RootState } from "types/state";

const actions = [employerConstants.GET_EMPLOYER_REQUIRED_STEPS];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    // selector reads a shallow-typed slice with a `[]` default (-> never[]);
    // the pending steps are string keys, so type the boundary as string[].
    pendingEmployerRequiredUpdates: pendingEmployerRequiredUpdates(
      state
    ) as string[],
    requiresAdministrativeUpdates: requiresAdministrativeUpdates(state),
  };
};

const mapDispatchToProps = {
  getEmployerRequiredUpdates,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  loading: boolean;
}

class EmployerRequiredUpdates extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: get(props, "isFetching", true),
    };
  }

  async componentDidMount() {
    await this.props
      // client is optional in WithApolloClient; original read it
      // unconditionally, so cast to preserve throw-on-undefined behavior
      .getEmployerRequiredUpdates(this.props.client as ApolloClient<object>)
      .then(() => {
        if (!this.props.requiresAdministrativeUpdates)
          this.props.push("/dashboard");
      })
      .then(() => {
        this.setState({ loading: false });
      });
  }

  render() {
    const { loading } = this.state;
    if (loading) {
      return <IconSpinner centered />;
    }
    const firstPendingStep = head(this.props.pendingEmployerRequiredUpdates);
    const components = {
      ICON_ORDER_FORM_AGREEMENT: (
        <OrderFormAgreements orderForm={"ICON_ORDER_FORM_AGREEMENT"} />
      ),
      ISOLVED_ORDER_FORM_AGREEMENT: (
        <OrderFormAgreements orderForm={"ISOLVED_ORDER_FORM_AGREEMENT"} />
      ),
    };
    // head() yields `string | undefined`; lodash get tolerates an undefined
    // path at runtime (returns undefined → renders the <div/> fallback), so
    // cast to satisfy the overload without changing behavior — from the JS.
    const component = get(components, firstPendingStep as string);

    return <>{!isEmpty(component) ? component : <div />}</>;
  }
}

export default connector(withApollo<Props>(EmployerRequiredUpdates));
